<template>
    <app-panel
        title="Dokumente"
        collapsible
        :default-open="true"
        panel-class="finance-document-panel"
        body-class="!p-0"
        footer-class="flex justify-end"
    >
        <div class="relative min-h-[60px]">
            <loading :status="loading"/>
            <div v-if="!loading && documents.length === 0" class="px-6 py-5 text-center text-sm text-gray-400 truncate">
                Keine Dokumente hinterlegt
            </div>
            <div
                ref="documentList"
                v-else
                class="finance-document-panel-list"
                :class="{'finance-document-panel-list--scrollable': documents.length > 6}"
                @scroll.passive="closeActionMenu"
            >
                <div v-for="(document, index) in documents" :key="document.id" class="px-6">
                    <div
                        class="flex items-center py-4"
                        :class="{'border-b border-ultralight': index < documents.length - 1}"
                    >
                        <span class="finance-document-panel-icon flex-shrink-0">
                            <svg-vue class="h-8 w-8" :icon="fileIcon(document)"></svg-vue>
                        </span>
                        <div class="ml-4 min-w-0 flex-1">
                            <div class="text-sm font-medium text-gray-800 truncate" :title="fileName(document)">{{ fileName(document) }}</div>
                            <div class="mt-0.5 text-xs text-gray-400 truncate">
                                {{ document.document_category_label }}
                                <template v-if="document.document_number"> · {{ document.document_number }}</template>
                            </div>
                        </div>
                        <div v-on-clickaway="closeActionMenu" class="relative ml-4 inline-flex flex-shrink-0">
                            <button
                                type="button"
                                class="inline-flex text-gray-400 focus:outline-none"
                                title="Aktionen"
                                aria-label="Aktionen"
                                @click.stop="toggleActionMenu(document, $event)"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <transition
                                duration="100"
                                enter-active-class="transition ease-out duration-100"
                                enter-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95"
                            >
                                <div
                                v-show="isActionMenuOpen(document)"
                                class="fixed z-[9999] w-52 origin-top-right rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                                :style="actionMenuPosition"
                                role="menu"
                                >
                                    <a
                                        :href="downloadUrl(document)"
                                        class="block w-full px-4 py-2 text-left text-sm text-gray-800 hover:bg-gray-100"
                                        role="menuitem"
                                        @click="closeActionMenu"
                                    >
                                        Herunterladen
                                    </a>
                                    <button
                                        v-if="canManage"
                                        type="button"
                                        class="block w-full px-4 py-2 text-left text-sm text-gray-800 hover:bg-gray-100"
                                        role="menuitem"
                                        @click.stop="openUnlinkDialog(document)"
                                    >
                                        Verknüpfung lösen
                                    </button>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template v-if="canManage" #footer>
            <button
                type="button"
                class="btn-stage-icon-plain"
                title="Dokument zuweisen"
                aria-label="Dokument zuweisen"
                @click="openDialog"
            >
                <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
            </button>
        </template>

        <app-dialog
            :open="dialog.open"
            title="Dokument zuweisen"
            title-id="finance-document-assignment-dialog-headline"
            size="md"
            :busy="submitting"
            @close="closeDialog"
        >
                        <div class="overflow-hidden rounded border border-bordergray-100">
                            <div class="relative border-b border-bordergray-100">
                                <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </div>
                                <input
                                    v-model="dialog.search"
                                    class="finance-document-assignment-search form-input block w-full pl-10"
                                    placeholder="Dokument suchen"
                                    @input="debouncedFetchAvailable"
                                >
                            </div>
                            <div class="finance-document-assignment-list">
                                <label
                                    v-for="document in availableDocuments"
                                    :key="document.id"
                                    class="finance-document-assignment-row flex cursor-pointer items-center border-b border-bordergray-100 px-4 py-3 last:border-b-0"
                                >
                                    <input v-model="dialog.selectedDocumentIds" type="checkbox" :value="document.id" class="form-checkbox h-4 w-4 flex-shrink-0 text-gray-800">
                                    <svg-vue class="ml-3 h-5 w-5 flex-shrink-0 finance-document-panel-icon" :icon="fileIcon(document)"></svg-vue>
                                    <span class="ml-3 flex min-w-0 flex-1 items-center gap-4">
                                        <span class="min-w-0 flex-1 truncate text-sm font-medium text-gray-800" :title="fileName(document)">
                                            {{ fileName(document) }}
                                        </span>
                                    <span class="flex-shrink-0 text-right text-sm text-ink-subtle">
                                            {{ document.document_category_label || '-' }}
                                        </span>
                                    </span>
                                </label>
                                <div v-if="availableDocuments.length === 0" class="px-4 py-6 text-center text-sm text-gray-400">Keine weiteren Dokumente gefunden.</div>
                            </div>
                        </div>

            <template #footer>
                <app-dialog-action
                    label="Verknüpfen"
                    loading-label="Wird verknüpft …"
                    :loading="submitting"
                    :disabled="dialog.selectedDocumentIds.length === 0"
                    @click="linkDocuments"
                />
            </template>
        </app-dialog>

        <confirm-dialog
            :open="unlinkDialog.open"
            title="Verknüpfung lösen"
            text="Soll die Verknüpfung dieses Dokuments zu diesem Finanzeintrag wirklich gelöst werden?"
            confirm-label="Verknüpfung lösen"
            confirm-variant="danger"
            headline-id="finance-document-unlink-dialog-headline"
            @close="closeUnlinkDialog"
            @confirm="unlinkDocument"
        />
    </app-panel>
</template>

<script>
import {getAuthToken} from '@/utils/auth-token';
import {resolveFileIcon} from '@/utils/workspace-file-manager';
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';

function emptyDialog() {
    return {
        open: false,
        search: '',
        selectedDocumentIds: [],
    };
}

export default {
    name: 'finance-document-panel',
    components: {AppDialog, AppDialogAction, ConfirmDialog},
    props: {
        entryId: {type: [String, Number], required: true},
    },
    data() {
        return {
            loading: false,
            submitting: false,
            token: getAuthToken(),
            documents: [],
            availableDocuments: [],
            canManage: false,
            dialog: emptyDialog(),
            searchTimer: null,
            actionMenuDocumentId: null,
            actionMenuPosition: null,
            unlinking: false,
            unlinkDialog: {open: false, document: null},
        };
    },
    mounted() {
        this.fetchDocuments();
        window.addEventListener('resize', this.closeActionMenu);
        window.addEventListener('scroll', this.closeActionMenu, true);
    },
    beforeDestroy() {
        window.clearTimeout(this.searchTimer);
        window.removeEventListener('resize', this.closeActionMenu);
        window.removeEventListener('scroll', this.closeActionMenu, true);
    },
    methods: {
        fetchDocuments() {
            this.loading = true;
            axios.get(`api/workspace/finance/${this.entryId}/documents`).then((response) => {
                this.documents = response.data.items || [];
                this.canManage = !!response.data.can_manage_documents;
            }).finally(() => {
                this.loading = false;
            });
        },
        fetchAvailable() {
            axios.get('api/workspace/finance-documents', {
                params: {perPage: 100, search: this.dialog.search, sort: {column: 'updated_at', order: 'desc'}},
            }).then((response) => {
                const linkedIds = new Set(this.documents.map((document) => Number(document.id)));
                this.availableDocuments = (response.data.items || []).filter((document) => !linkedIds.has(Number(document.id)));
            });
        },
        debouncedFetchAvailable() {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(() => this.fetchAvailable(), 250);
        },
        openDialog() {
            this.dialog = emptyDialog();
            this.dialog.open = true;
            this.fetchAvailable();
        },
        closeDialog() {
            this.dialog = emptyDialog();
        },
        closeActionMenu() {
            this.actionMenuDocumentId = null;
            this.actionMenuPosition = null;
        },
        toggleActionMenu(document, event) {
            const documentId = Number(document && document.id);
            if (this.actionMenuDocumentId === documentId) {
                this.closeActionMenu();
                return;
            }

            const trigger = event && event.currentTarget;
            if (trigger) {
                const triggerRect = trigger.getBoundingClientRect();
                const menuWidth = 208;
                const menuHeight = this.canManage ? 88 : 48;
                const viewportGap = 8;
                const spaceBelow = window.innerHeight - triggerRect.bottom;
                const openAbove = spaceBelow < menuHeight + viewportGap && triggerRect.top > menuHeight + viewportGap;
                const top = openAbove
                    ? triggerRect.top - menuHeight - viewportGap
                    : triggerRect.bottom + viewportGap;
                const left = Math.min(
                    Math.max(viewportGap, triggerRect.right - menuWidth),
                    window.innerWidth - menuWidth - viewportGap
                );

                this.actionMenuPosition = {
                    top: `${Math.max(viewportGap, top)}px`,
                    left: `${left}px`,
                };
            }

            this.actionMenuDocumentId = documentId;
        },
        isActionMenuOpen(document) {
            return this.actionMenuDocumentId === Number(document && document.id);
        },
        openUnlinkDialog(document) {
            this.closeActionMenu();
            this.unlinkDialog = {open: true, document};
        },
        closeUnlinkDialog() {
            if (this.unlinking) return;
            this.unlinkDialog = {open: false, document: null};
        },
        linkDocuments() {
            if (this.submitting || this.dialog.selectedDocumentIds.length === 0) return;
            this.submitting = true;
            Promise.all(this.dialog.selectedDocumentIds.map((documentId) => (
                axios.post(`api/workspace/finance/${this.entryId}/documents/${documentId}`)
            ))).then(() => {
                this.closeDialog();
                this.fetchDocuments();
                this.$emit('history-changed');
                this.$notify({title: 'Erfolg', text: 'Dokumente wurden verknüpft.', type: 'success'});
            }).finally(() => {
                this.submitting = false;
            });
        },
        unlinkDocument() {
            const document = this.unlinkDialog.document;
            if (!document || this.unlinking) return;
            this.unlinking = true;
            axios.delete(`api/workspace/finance/${this.entryId}/documents/${document.id}`).then(() => {
                this.unlinkDialog = {open: false, document: null};
                this.fetchDocuments();
                this.$emit('history-changed');
                this.$notify({title: 'Erfolg', text: 'Verknüpfung wurde entfernt.', type: 'success'});
            }).finally(() => {
                this.unlinking = false;
            });
        },
        fileName(document) {
            return document && document.file ? document.file.name : 'Dokument';
        },
        fileIcon(document) {
            return resolveFileIcon(document && document.file ? document.file.extension : '');
        },
        downloadUrl(document) {
            const file = document && document.file ? document.file : {};
            return `${file.download || '#'}?token=${this.token}`;
        },
    },
};
</script>

<style lang="scss">
.finance-document-panel-icon { color: var(--ui-text-subtle); }

.finance-document-panel-list {
    overflow: visible;
}

.finance-document-panel-list--scrollable {
    max-height: 26.25rem;
    overflow-y: auto;
}

.finance-document-assignment-search,
.finance-document-assignment-search:focus {
    border: 0 !important;
    border-radius: 0;
    color: var(--ui-text-strong);
    box-shadow: none;
}

.finance-document-assignment-list {
    max-height: 22.5rem;
    overflow-y: auto;
}

.finance-document-assignment-row:nth-child(even) {
    background: var(--ui-surface-raised);
}
</style>
